/**
 * Cyber Risk Signal (CRS) Dashboard Component
 *
 * Board-ready composite risk indicator showing:
 * - Overall CRS score (0-100) with visual dial
 * - Risk band (stable/elevated/high/critical)
 * - Momentum indicator (improving/flat/deteriorating)
 * - Peer comparison
 * - Decision triggers requiring attention
 * - Historical trend sparkline
 *
 * @version 1.0.0
 * @created December 19, 2025
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import HelpTooltip from '../../../../shared/components/HelpTooltip';
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Users,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Zap
} from 'lucide-react';
import apiService from '../../../../services/api';
import { getCrsBandStyle, legacyCrsBandToLabel, getRiskBandLabel, getQuartileLabel } from '../../../../utils/bands';

interface CRSData {
  score: number;
  band: 'stable' | 'elevated' | 'high' | 'critical';
  momentum: 'improving' | 'flat' | 'deteriorating';
  momentumDelta: number;
  peerPosition: {
    percentile: number;
    label: string;
    comparison?: string;
  };
  triggers: string[];
  triggerCount: number;
  headline: string;
  executiveSummary: string;
  components: {
    externalPressure: number;
    internalFragility: number;
    governanceRisk: number;
    trajectory: number;
    organizationalStress: number;
  };
  industry: string;
  calculatedAt: string;
}

interface CRSHistoryItem {
  score: number;
  band: string;
  momentum: string;
  calculatedAt: string;
}

interface CyberRiskSignalProps {
  sessionId: string;
  compact?: boolean;
}

const CyberRiskSignal: React.FC<CyberRiskSignalProps> = ({ sessionId, compact = false }) => {
  const [crsData, setCrsData] = useState<CRSData | null>(null);
  const [history, setHistory] = useState<CRSHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch CRS data
  const fetchCRS = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const [crsResponse, historyResponse] = await Promise.all([
        apiService.getCyberRiskSignal(sessionId, refresh),
        apiService.getCyberRiskSignalHistory(sessionId, 7)
      ]);

      if (crsResponse.success) {
        setCrsData(crsResponse.data);
      }
      if (historyResponse.success) {
        setHistory(historyResponse.data);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load Cyber Risk Signal');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchCRS();
    }
  }, [sessionId]);

  // Color schemes from centralized band utility (NIST-aligned: Low/Moderate/High/Critical)
  const bandStyle = getCrsBandStyle(crsData?.band || 'stable');
  const colors = {
    stroke: bandStyle.stroke,
    text: bandStyle.text,
    bg: bandStyle.bg,
    border: bandStyle.border,
    label: bandStyle.label,
    icon: bandStyle.icon,
  };

  const getMomentumIcon = (momentum: string) => {
    switch (momentum) {
      case 'improving':
        return <TrendingDown aria-hidden="true" className="h-4 w-4 text-emerald-500" />;
      case 'deteriorating':
        return <TrendingUp aria-hidden="true" className="h-4 w-4 text-red-500" />;
      default:
        return <Minus aria-hidden="true" className="h-4 w-4 text-[#8892b0]" />;
    }
  };

  const getMomentumLabel = (momentum: string, delta: number) => {
    switch (momentum) {
      case 'improving':
        return `Improving ${Math.abs(delta)}pts`;
      case 'deteriorating':
        return `Deteriorating +${Math.abs(delta)}pts`;
      default:
        return 'Stable';
    }
  };

  if (loading) {
    return (
      <div className="card p-6 animate-pulse">
        <div className="flex items-center space-x-3">
          <div className="w-20 h-20 rounded-full bg-[#1e3148]" />
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-[#1e3148] rounded w-3/4" />
            <div className="h-3 bg-[#1e3148] rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6 border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)]">
        <div className="flex items-center text-[#f87171]">
          <AlertTriangle aria-hidden="true" className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // No signal: the backend computes none without a full Big 6 (Pat, 2026-10-19). Compass sits
  // behind the completeness gate, so the viewer has finished the assessment — the copy must not
  // say "not completed". Say why, and show the dimensions as pending.
  //
  // ⛔ Render NO value here, not even a dimmed or placeholder one. This panel states risk as a
  // BAND, and getRiskBand(0) returns "Low" — so a missing dimension used to present itself as a
  // reassuring verdict about a customer who has answered nothing. An absent measurement is not a
  // good one. Guard: CyberRiskSignal.emptyState.test.tsx asserts no band label and no digits.
  if (!crsData) {
    return (
      <div className="card p-6">
        <h3 className="text-base font-medium text-[#ccd6f6]">Cyber Risk Signal&trade;</h3>
        <p className="mt-3 text-sm text-[#a8b2d1]">
          No Cyber Risk Signal yet. The signal needs a score in all six risk categories, and
          not every category could be scored for this assessment.
        </p>
        <div className="mt-5 space-y-3" aria-label="Pending signal components">
          {[
            'External Pressure',
            'Internal Fragility',
            'Governance Risk',
            'Trajectory',
            'Organizational Stress',
          ].map((label) => (
            <div key={label} className="flex items-center">
              <span className="text-sm text-[#a8b2d1] w-40">{label}</span>
              <span className="text-sm text-[#6b7a99]" aria-label="Not yet measured">
                &mdash;
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const radius = compact ? 32 : 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (crsData.score / 100) * circumference;

  // Sparkline for history
  const renderSparkline = () => {
    if (history.length < 2) return null;

    const maxScore = Math.max(...history.map(h => h.score));
    const minScore = Math.min(...history.map(h => h.score));
    const range = maxScore - minScore || 1;
    const width = 80;
    const height = 24;

    const points = history
      .slice()
      .reverse()
      .map((h, i) => {
        const x = (i / (history.length - 1)) * width;
        const y = height - ((h.score - minScore) / range) * height;
        return `${x},${y}`;
      })
      .join(' ');

    return (
      <svg width={width} height={height} className="ml-2">
        <polyline
          points={points}
          fill="none"
          stroke={colors.stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  // Compact view for widgets
  if (compact) {
    return (
      <div className="card p-4 border-l-4" style={{ borderLeftColor: colors.stroke }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="relative w-16 h-16">
              <svg className="w-full h-full">
                <circle
                  cx="32"
                  cy="32"
                  r={radius}
                  strokeWidth="6"
                  stroke="rgba(0,0,0,0.1)"
                  fill="none"
                />
              </svg>
              <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                <motion.circle
                  cx="32"
                  cy="32"
                  r={radius}
                  strokeWidth="6"
                  stroke={colors.stroke}
                  fill="none"
                  strokeLinecap="round"
                  initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold" style={{ color: colors.text }}>{colors.icon}</span>
              </div>
            </div>
            <div className="ml-3">
              <div className="flex items-center">
                <span className="text-sm font-semibold" style={{ color: colors.text }}>
                  {colors.label}
                </span>
                <span className="mx-2 text-[#5a6a8a]">|</span>
                <span className="flex items-center text-sm text-[#a8b2d1]">
                  {getMomentumIcon(crsData.momentum)}
                  <span className="ml-1">{getMomentumLabel(crsData.momentum, crsData.momentumDelta)}</span>
                </span>
              </div>
              <p className="text-xs text-[#8892b0] mt-1">
                {getQuartileLabel(crsData.peerPosition.percentile)} vs {crsData.industry} peers
              </p>
            </div>
          </div>
          {crsData.triggerCount > 0 && (
            <div className="flex items-center bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-medium">
              <Zap aria-hidden="true" className="h-3 w-3 mr-1" />
              {crsData.triggerCount} trigger{crsData.triggerCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b" style={{ backgroundColor: colors.bg, borderBottomColor: colors.border }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Activity aria-hidden="true" className="h-5 w-5 mr-2" style={{ color: colors.text }} />
            <h2 className="text-lg font-semibold text-[#e6f1ff] flex items-center gap-1.5">Cyber Risk Signal™ <HelpTooltip contentKey="compass.crs.score" /></h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => fetchCRS(true)}
              disabled={refreshing}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              title="Refresh CRS"
            >
              <RefreshCw aria-hidden="true" className={`h-4 w-4 text-[#8892b0] ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: colors.bg, color: colors.text }}>
              {colors.icon} {colors.label}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <div className="flex items-start justify-between">
          {/* Risk Dial — shows band label instead of numeric score */}
          <div className="flex items-center">
            <div className="relative w-28 h-28">
              <svg className="w-full h-full">
                <circle
                  cx="56"
                  cy="56"
                  r={radius}
                  strokeWidth="8"
                  stroke="rgba(0,0,0,0.1)"
                  fill="none"
                />
              </svg>
              <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                <motion.circle
                  cx="56"
                  cy="56"
                  r={radius}
                  strokeWidth="8"
                  stroke={colors.stroke}
                  fill="none"
                  strokeLinecap="round"
                  initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold" style={{ color: colors.text }}>{colors.label}</span>
              </div>
            </div>

            <div className="ml-6">
              {/* Momentum */}
              <div className="flex items-center mb-2">
                {getMomentumIcon(crsData.momentum)}
                <span className="ml-2 text-sm font-medium text-[#ccd6f6]">
                  {getMomentumLabel(crsData.momentum, crsData.momentumDelta)}
                </span>
              </div>

              {/* Peer Position — quartile label only, no percentile number */}
              <div className="flex items-center text-sm text-[#a8b2d1]">
                <Users aria-hidden="true" className="h-4 w-4 mr-2 text-[#5a6a8a]" />
                <span>{getQuartileLabel(crsData.peerPosition.percentile)} vs {crsData.industry} peers</span>
              </div>

              {/* Trend Sparkline */}
              <div className="flex items-center mt-2">
                <span className="text-xs text-[#8892b0]">7-day trend</span>
                {renderSparkline()}
              </div>
            </div>
          </div>

          {/* Triggers */}
          {crsData.triggerCount > 0 && (
            <div className="bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.3)] rounded-lg p-3 max-w-xs">
              <div className="flex items-center text-[#f87171] font-medium text-sm mb-2">
                <Zap aria-hidden="true" className="h-4 w-4 mr-1" />
                {crsData.triggerCount} Decision Trigger{crsData.triggerCount > 1 ? 's' : ''}
              </div>
              <ul className="space-y-1">
                {crsData.triggers.slice(0, 3).map((trigger, i) => (
                  <li key={i} className="text-xs text-red-700 flex items-start">
                    <span className="mr-1">•</span>
                    {trigger}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Headline */}
        <div className="mt-6 p-4 bg-[rgba(255,255,255,0.03)] rounded-lg">
          <p className="text-sm text-[#ccd6f6] font-medium">{crsData.headline}</p>
        </div>

        {/* CRS Details — always visible */}
        <div className="mt-4 space-y-4">
            {/* Executive Summary */}
            <div className="p-4 bg-[rgba(0,217,255,0.06)] border border-[rgba(0,217,255,0.25)] rounded-lg">
              <h4 className="text-sm font-medium text-[#ccd6f6] mb-2">Executive Summary</h4>
              <p className="text-sm text-blue-700">{crsData.executiveSummary}</p>
            </div>

            {/* Component Breakdown — band labels, no weights */}
            <div>
              <h4 className="text-sm font-medium text-[#ccd6f6] mb-3 flex items-center gap-1.5">CRS Components <HelpTooltip contentKey="compass.crs.components" side="right" /></h4>
              <div className="space-y-3">
                {/*
                  ⛔ Do NOT coalesce an absent dimension to 0. getRiskBand(0) returns "Low", so
                  `?? 0` rendered a dimension the engine never produced as a reassuring LOW-risk
                  verdict — absence reading as good news on a risk scale. Unmeasured stays
                  unmeasured: an em dash, an empty bar, no colour.
                  Guard: CyberRiskSignal.emptyState.test.tsx
                */}
                {[
                  { label: 'External Pressure', value: crsData.components?.externalPressure },
                  { label: 'Internal Fragility', value: crsData.components?.internalFragility },
                  { label: 'Governance Risk', value: crsData.components?.governanceRisk },
                  { label: 'Trajectory', value: crsData.components?.trajectory },
                  { label: 'Organizational Stress', value: crsData.components?.organizationalStress }
                ].map((component) => {
                  const measured = typeof component.value === 'number' && Number.isFinite(component.value);
                  const compBand = measured ? getRiskBandLabel(component.value as number) : '—';
                  const compStyle = measured
                    ? getCrsBandStyle(component.value as number)
                    : { stroke: 'transparent', text: '#6b7a99' };
                  return (
                    <div key={component.label} className="flex items-center">
                      <span className="text-sm text-[#a8b2d1] w-40">{component.label}</span>
                      <div className="flex-1 mx-3">
                        <div className="h-2 bg-[#1e3148] rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ backgroundColor: compStyle.stroke }}
                            initial={{ width: 0 }}
                            animate={{ width: measured ? `${component.value}%` : '0%' }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium w-20 text-right" style={{ color: compStyle.text }}>{compBand}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Last Updated */}
            <div className="text-xs text-[#5a6a8a] text-right">
              Last calculated: {new Date(crsData.calculatedAt).toLocaleString()}
            </div>
        </div>
      </div>
    </div>
  );
};

export default CyberRiskSignal;
